import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Dialog, IssueIcon, Text } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import { useCycleStore } from '../store'

type HoldStepInfo = {
	stepId: string
	stepName: string
	evaluationCount: number
}

export function CycleHoldManagementTab() {
	const { organizationId } = useParams<{ organizationId: string }>()
	const [releaseDialogOpen, setReleaseDialogOpen] = useState(false)
	const [selectedStep, setSelectedStep] = useState<HoldStepInfo | null>(null)
	const queryClient = useQueryClient()
	const { showToast } = useToast()

	const cycle = useCycleStore((state) => state.cycle)

	const {
		data: holdSteps,
		isLoading,
		isError,
	} = useQuery({
		...trpc.admin.cycles.participants.hold.getSteps.queryOptions({
			organizationId: organizationId as string,
			cycleId: cycle?.id || '',
		}),
		enabled: !!cycle?.id && !!organizationId,
	})

	const releaseHoldMutation = useMutation({
		...trpc.admin.cycles.participants.hold.release.mutationOptions(),
		onSuccess: (data) => {
			showToast(data.message || 'Hold released successfully')
			setReleaseDialogOpen(false)
			setSelectedStep(null)
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.participants.hold.getSteps.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
		onError: () => {
			showToast('Failed to release hold. Please try again.', 'error')
		},
	})

	const handleReleaseClick = (step: HoldStepInfo) => {
		setSelectedStep(step)
		setReleaseDialogOpen(true)
	}

	const handleConfirmRelease = () => {
		if (!selectedStep || !cycle || !organizationId) return
		releaseHoldMutation.mutate({
			organizationId,
			cycleId: cycle.id,
			cycleStepId: selectedStep.stepId,
		})
	}

	const handleCloseDialog = () => {
		setReleaseDialogOpen(false)
		setSelectedStep(null)
	}

	const hasHoldSteps = holdSteps && holdSteps.length > 0

	return (
		<div className='mt-6'>
			<div className='border border-neutral-100 bg-neutral-0 rounded-lg shadow-xl p-6'>
				<div className='mb-4'>
					<Text variant='h6' weight='semibold'>
						Manage Hold Steps
					</Text>
					<Text variant='small' className='text-neutral-500 mt-1'>
						Release evaluations that are currently waiting at hold steps. When you release a hold, all evaluations at
						that step will progress to the next step in the cycle.
					</Text>
				</div>

				{isLoading ? (
					<div className='flex items-center justify-center p-8'>
						<Text variant='small' className='text-neutral-500'>
							Loading hold steps...
						</Text>
					</div>
				) : isError ? (
					<div className='flex items-center justify-center p-8'>
						<Text variant='small' className='text-red-600'>
							Failed to load hold steps. Please try again.
						</Text>
					</div>
				) : hasHoldSteps ? (
					<div className='space-y-3'>
						{holdSteps.map((step) => (
							<div
								key={step.stepId}
								className='flex items-center justify-between p-4 border border-neutral-200 rounded-lg bg-white'>
								<div className='flex flex-col'>
									<Text variant='small' weight='semibold'>
										{step.stepName}
									</Text>
									<Text variant='xsmall' className='text-neutral-500'>
										{step.evaluationCount} evaluation{step.evaluationCount !== 1 ? 's' : ''} waiting
									</Text>
								</div>
								<Button
									variant='primary'
									size='small'
									onClick={() => handleReleaseClick(step)}
									disabled={releaseHoldMutation.isPending || releaseDialogOpen}>
									Release Hold
								</Button>
							</div>
						))}
					</div>
				) : (
					<div className='flex flex-col items-center justify-center p-8 border border-neutral-200 rounded-lg bg-neutral-50'>
						<IssueIcon className='w-12 h-12 fill-neutral-300 mb-3' />
						<Text variant='small' weight='semibold' className='text-neutral-600'>
							No evaluations waiting at hold steps
						</Text>
						<Text variant='xsmall' className='text-neutral-500 text-center mt-1'>
							When evaluations reach a hold step in the cycle, they will appear here for you to release.
						</Text>
					</div>
				)}
			</div>

			<Dialog
				isOpen={releaseDialogOpen}
				onClose={handleCloseDialog}
				onConfirm={handleConfirmRelease}
				title='Release Hold'
				message={
					selectedStep
						? `Are you sure you want to release the hold on "${selectedStep.stepName}"? This will progress ${selectedStep.evaluationCount} evaluation${selectedStep.evaluationCount !== 1 ? 's' : ''} to the next step.`
						: ''
				}
				type='confirm'
				confirmLabel={releaseHoldMutation.isPending ? 'Releasing...' : 'Release Hold'}
			/>
		</div>
	)
}
